/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';

/**
 * Prompt shown when ops picks the cancelled status.
 *
 * Both status controls (the orders-table dropdown and the order-edit form) fire their PUT
 * immediately on change, so this deliberately INTERCEPTS: the status change is held until
 * a reason is supplied, then the caller sends status + reason together. Dismissing it must
 * revert the select to the previous value, otherwise the UI would show "cancelled" for an
 * order that was never actually cancelled.
 *
 * @param {function} onConfirm  called with the trimmed reason
 * @param {function} onDismiss  called when ops backs out — caller reverts the select
 * @param {boolean}  saving     disables the controls while the request is in flight
 * @param {string}   variant    'popover' floats out of a narrow table cell; 'inline' sits
 *                              in the flow of a form that already has width to spare
 */
const CancellationReasonPrompt = ({ onConfirm, onDismiss, saving = false, variant = 'popover' }) => {
    const [reason, setReason] = useState('');
    const inputRef = useRef(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const trimmed = reason.trim();

    // The reason is optional — ops can cancel without one. Callers omit the field entirely
    // when it is blank rather than persisting an empty string.
    const confirm = () => {
        if (saving) return;
        onConfirm(trimmed);
    };

    // A table cell is far too narrow to lay this out in flow, so float above it at a
    // readable fixed width. The form variant has room and stays in flow.
    const shell = variant === 'popover'
        ? 'absolute right-0 top-full z-50 mt-2 w-[290px]'
        : 'relative mt-2 w-full max-w-sm';

    return (
        <div
            className={`${shell} rounded-xl border border-white-200 bg-white-50 p-3 text-left shadow-soft-3xl font-sans`}
            // The table row is clickable — keep interaction local to the prompt.
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Cancellation reason"
        >
            <p className="text-sm font-semibold leading-snug text-cod-gray-900">
                Cancel this order?
            </p>
            <p className="mt-0.5 text-xs text-cod-gray-500">
                Add a reason if you have one — it is shared with the partner.
            </p>

            <input
                ref={inputRef}
                type="text"
                value={reason}
                disabled={saving}
                placeholder="e.g. Out of stock (optional)"
                className="mt-2 w-full rounded-lg border border-white-200 px-3 py-2 text-sm text-cod-gray-900 placeholder:text-cod-gray-300 focus:border-elm-400 focus:outline-none focus:ring-1 focus:ring-elm-400 disabled:opacity-50"
                onChange={(e) => setReason(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') confirm();
                    if (e.key === 'Escape') onDismiss();
                }}
            />

            <div className="mt-3 flex flex-row items-center gap-2">
                <button
                    type="button"
                    disabled={saving}
                    onClick={confirm}
                    className="whitespace-nowrap rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white-50 transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                    {saving ? 'Cancelling…' : 'Cancel order'}
                </button>
                <button
                    type="button"
                    disabled={saving}
                    onClick={onDismiss}
                    className="whitespace-nowrap rounded-lg border border-white-200 px-3 py-2 text-xs font-semibold text-cod-gray-600 transition-colors hover:bg-white-100 disabled:opacity-40"
                >
                    Keep order
                </button>
            </div>
        </div>
    );
};

export default CancellationReasonPrompt;
